import React, { useEffect, useState } from "react";
import { Plus, Save, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Card, SectionTitle, DemoTag } from "@/components/ui/page";

const emptyPolicy = { name: "", scope: "global", scope_ref: "", short_break: 15, lunch: 45, personal: 15, meeting: 60, daily_total: 60, warning_threshold: 5 };
const FIELDS = [
  { k: "short_break", label: "Short Break" }, { k: "lunch", label: "Lunch" }, { k: "personal", label: "Personal" },
  { k: "meeting", label: "Meeting / Training" }, { k: "daily_total", label: "Daily Total" }, { k: "warning_threshold", label: "Warning (min before)" },
];

export default function Policies() {
  const [policies, setPolicies] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [edit, setEdit] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/policies").then((r) => setPolicies(r.data));
  useEffect(() => {
    load();
    api.get("/processes").then((r) => setProcesses(r.data));
    api.get("/shifts").then((r) => setShifts(r.data));
  }, []);

  const save = async (p, isNew) => {
    try {
      const body = { ...p, scope_ref: p.scope === "global" ? null : p.scope_ref };
      FIELDS.forEach((f) => (body[f.k] = Number(body[f.k])));
      if (isNew) { await api.post("/policies", body); toast.success("Policy created"); setAdding(false); }
      else { await api.put(`/policies/${p.id}`, body); toast.success("Policy updated"); setEdit(null); }
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Break Policies" subtitle="Configure break allowances (minutes) globally or by process/shift" testId="policies-header">
        <button data-testid="add-policy-btn" onClick={() => setAdding(true)} className="flex items-center gap-2 rounded-lg bg-[#08A7A0] px-4 py-2 text-sm font-bold text-white hover:bg-[#06827C]"><Plus className="h-4 w-4" /> New Policy</button>
      </PageHeader>

      {adding && <PolicyForm initial={{ ...emptyPolicy }} processes={processes} shifts={shifts} onSave={(p) => save(p, true)} onCancel={() => setAdding(false)} isNew />}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {policies.map((p) => (
          edit?.id === p.id
            ? <PolicyForm key={p.id} initial={edit} processes={processes} shifts={shifts} onSave={(x) => save(x, false)} onCancel={() => setEdit(null)} />
            : (
              <Card key={p.id} className="p-5" testId={`policy-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div><SectionTitle>{p.name} {p.is_demo && <DemoTag />}</SectionTitle>
                    <p className="mt-0.5 text-xs uppercase tracking-wide text-[#08A7A0]">{p.scope}{p.scope_ref ? ` · ${p.scope_ref}` : ""}</p>
                  </div>
                  <button data-testid={`edit-policy-${p.id}`} onClick={() => setEdit(p)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Edit</button>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {FIELDS.map((f) => (
                    <div key={f.k} className="rounded-lg bg-slate-50 p-3 text-center">
                      <p className="font-display text-2xl font-extrabold text-[#0B1F33]">{p[f.k]}</p>
                      <p className="mt-0.5 text-[11px] font-medium text-slate-500">{f.label}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )
        ))}
      </div>
    </div>
  );
}

function PolicyForm({ initial, processes, shifts, onSave, onCancel, isNew }) {
  const [p, setP] = useState(initial);
  const set = (k, v) => setP({ ...p, [k]: v });
  const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#08A7A0]";
  return (
    <Card className="mb-4 p-5 ring-2 ring-[#08A7A0]/30" testId="policy-form">
      <div className="flex items-center justify-between">
        <SectionTitle>{isNew ? "New Break Policy" : `Edit ${p.name}`}</SectionTitle>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="sm:col-span-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input data-testid="policy-name" value={p.name} onChange={(e) => set("name", e.target.value)} placeholder="Policy name" className={inputCls} />
          <select data-testid="policy-scope" value={p.scope} onChange={(e) => set("scope", e.target.value)} className={inputCls}>
            <option value="global">Global</option><option value="process">Process</option><option value="shift">Shift</option>
          </select>
          {p.scope !== "global" && (
            <select data-testid="policy-scope-ref" value={p.scope_ref || ""} onChange={(e) => set("scope_ref", e.target.value)} className={inputCls}>
              <option value="">Select {p.scope}</option>
              {(p.scope === "process" ? processes : shifts).map((x) => <option key={x.id} value={x.name}>{x.name}</option>)}
            </select>
          )}
        </div>
        {FIELDS.map((f) => (
          <div key={f.k}>
            <label className="text-xs font-semibold text-slate-500">{f.label} (min)</label>
            <input data-testid={`policy-${f.k}`} type="number" value={p[f.k]} onChange={(e) => set(f.k, e.target.value)} className={`mt-1 ${inputCls}`} />
          </div>
        ))}
      </div>
      <button data-testid="save-policy" onClick={() => onSave(p)} className="mt-4 flex items-center gap-2 rounded-lg bg-[#08A7A0] px-4 py-2.5 text-sm font-bold text-white"><Save className="h-4 w-4" /> Save Policy</button>
    </Card>
  );
}
